import { useLogger } from '@ori/logger';
import { StaticNavigationNavigationItemDtoType } from '@ori/static-api-client-react';
import type { StaticNavigationNavigationItemDto } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useFetchNavigationItems } from '../../hooks/useFetchNavigationItems';

export interface LabelsContext {
  aboutLabel: string;
  joinLabel: string;
  joinUrl: string;
  loading: boolean;
}

export const labelsContextDefaultValue: LabelsContext = {
  aboutLabel: '',
  joinLabel: '',
  joinUrl: '',
  loading: true,
};

export const labelsContext = createContext(labelsContextDefaultValue);

const { Provider } = labelsContext;

const findLabel = (
  item: StaticNavigationNavigationItemDtoType,
  collection?: StaticNavigationNavigationItemDto[] | null,
) => collection?.find(({ type }) => type === item)?.title ?? '';

const findUrl = (
  item: StaticNavigationNavigationItemDtoType,
  collection?: StaticNavigationNavigationItemDto[] | null,
) => collection?.find(({ type }) => type === item)?.url ?? '';

export const LabelsProvider: FC<PropsWithChildren> = ({ children }) => {
  const logger = useLogger('LabelsProvider');
  const [aboutLabel, setAboutLabel] = useState(labelsContextDefaultValue.aboutLabel);
  const [joinLabel, setJoinLabel] = useState(labelsContextDefaultValue.joinLabel);
  const [joinUrl, setJoinUrl] = useState(labelsContextDefaultValue.joinUrl);
  const [loading, setLoading] = useState(true);
  const fetchNavigationItems = useFetchNavigationItems();
  useEffect(() => {
    fetchNavigationItems(StaticNavigationNavigationItemDtoType.TopNavigation)
      .then((items) => {
        setAboutLabel(findLabel(StaticNavigationNavigationItemDtoType.AboutTiles, items));
        setJoinLabel(findLabel(StaticNavigationNavigationItemDtoType.JoinTiles, items));
        setJoinUrl(findUrl(StaticNavigationNavigationItemDtoType.JoinTiles, items));
      })
      .catch((error) => logger.error(new Error('Cannot fetch navigation items', { cause: error })))
      .finally(() => setLoading(false));
  }, [fetchNavigationItems, logger]);
  const value = useMemo(() => ({ aboutLabel, joinLabel, joinUrl, loading }), [aboutLabel, joinLabel, joinUrl, loading]);

  return <Provider value={value}>{children}</Provider>;
};

export const useLabels = () => useContext(labelsContext);
